import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import translate from '../../../../commonComponents/translate';
import ProfileField from './profile/ProfileField';
import AddressProp from '../../../../commonComponents/propTypes/addressProp';
import { compose, PROFILE_BLOCK } from '../../../../services/utilityFunctions';
import DirectSellingModel from '../../../../commonComponents/DirectSellingModel';
import OrganizationDirectSelling from '../../../../commonComponents/propTypes/OrganizationDirectSelling';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getValidationRules } from '../../helpers/helper';

function stateFromProps(member = {}) {
  return {
    workplace: member.workplace || '',
    workplaceLocation:
      member.workplaceLocation && member.workplaceLocation.id
        ? member.workplaceLocation.id
        : '',
    company: member.company || '',
    position: member.position || '',
    title: member.title || '',
    startedWorking: member.startedWorking,
    notes: member.notes || '',
    directSelling: member?.directSelling || {},
    commissionCoefficient: member?.directSelling?.commissionCoefficient
      ? member.directSelling.commissionCoefficient
      : null,
  };
}

/**
 * Form to update the current logged user
 */
function CompanyForm({
  t,
  onMemberChange,
  member,
  workplaceLocations,
  directSellingModel,
  onSkipStep,
}) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const { list, enabled } = workplaceLocations;
  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);
  const isWeelodge =
    directSellingModel ===
    OrganizationDirectSelling.DIRECT_SELLING_TYPE.WEELODGE;
  const commissionCoefficient = state.commissionCoefficient
    ? state.commissionCoefficient
    : `${t('profile_commissionCoefficientDoesNotExist')}`;
  const formId = 'companyForm';
  const validationRules = useMemo(() => {
    const validation = {};
    return getValidationRules(
      requiredFieldsPerBlock,
      PROFILE_BLOCK.COMPANY,
      validation,
    );
  }, [getValidationRules, requiredFieldsPerBlock]);

  const handleSubmit = async () => {
    let address = null;
    if (enabled && state.workplaceLocation) {
      address = list.find(addr => addr.id === state.workplaceLocation);
    }
    const params = {
      workplace: !enabled ? state.workplace : null,
      workplaceLocation: enabled ? address : null,
      company: state.company,
      title: state.title,
      startedWorking: state.startedWorking,
      notes: state.notes,
    };
    onMemberChange(params);
  };

  const handleChange = useCallback(
    (field, value) => setState(prevState => ({ ...prevState, [field]: value })),
    [],
  );

  const inputProps = useCallback(field => {
    if (field === 'workplaceLocation') {
      return {
        items: list.map(addr => ({
          id: addr.id,
          text: addr.address,
        })),
        allowClear: true,
      };
    }
    if (field === 'commissionCoefficient') {
      return { disabled: true };
    }
    if (field !== 'startedWorking') {
      return undefined;
    }
    return { startView: 'year', endDate: Date.now() };
  }, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_company')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            {!enabled ? (
              <ProfileField
                field="workplace"
                value={state.workplace}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.COMPANY}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
            ) : (
              <>
                <ProfileField
                  field="workplaceLocation"
                  value={state.workplaceLocation}
                  onChange={handleChange}
                  inputProps={inputProps}
                  type="select"
                  block={PROFILE_BLOCK.COMPANY}
                  requiredFieldsPerBlock={requiredFieldsPerBlock}
                />
                {isWeelodge && (
                  <ProfileField
                    field="commissionCoefficient"
                    value={commissionCoefficient}
                    inputProps={inputProps}
                    hint={`${t('profile_commissionCoefficient_hint')}`}
                  />
                )}
              </>
            )}
            <ProfileField
              field="company"
              value={state.company}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.COMPANY}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="title"
              value={state.title}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.COMPANY}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="startedWorking"
              value={state.startedWorking}
              onChange={handleChange}
              type="date"
              inputProps={inputProps}
              block={PROFILE_BLOCK.COMPANY}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="notes"
              value={state.notes}
              onChange={handleChange}
              type="textarea"
              inputProps={inputProps}
              block={PROFILE_BLOCK.COMPANY}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

CompanyForm.propTypes = {
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  workplaceLocations: PropTypes.shape({
    enabled: PropTypes.bool,
    list: PropTypes.arrayOf(AddressProp.propType),
  }).isRequired,
  directSellingModel: PropTypes.string,
  onSkipStep: PropTypes.func,
};

CompanyForm.defaultProps = {
  directSellingModel: null,
  onSkipStep: null,
};
export default compose(DirectSellingModel, translate)(CompanyForm);
